import React from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Story } from '@/utils/storiesData';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';

interface StoryCardProps {
  story: Story;
  featured?: boolean;
  aspectRatio?: string;
  disableAnimation?: boolean;
  hideAuthor?: boolean;
  useBrowseMobileStyle?: boolean;
}

const StoryCard: React.FC<StoryCardProps> = ({
  story,
  featured = false,
  aspectRatio,
  disableAnimation = false,
  hideAuthor = true,
  useBrowseMobileStyle = false
}) => {
  const { resolvedTheme } = useTheme();
  const isMobile = useIsMobile();
  
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const metaTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  const badgeBorderColor = resolvedTheme === 'dark' ? 'border-[#B0B0B0]' : 'border-[#252424]';

  // Determine if we're in the Browse route to apply the Chroma font
  const isBrowseRoute = window.location.pathname.includes('/browse');
  const headerFontClass = isBrowseRoute ? 'font-chroma' : 'font-chroma';

  if (isMobile) {
    // Use different width for browse pages vs index pages
    const cardWidth = useBrowseMobileStyle ? "w-full" : "w-[90%]";
    
    return (
      <Link to={`/stories/${story.slug}`} className={`flex-shrink-0 ${cardWidth} snap-start mx-0 px-0`}>
        <div className="relative overflow-hidden rounded-[2px] aspect-[3/2]">
          <img 
            src={story.coverImage} 
            alt={story.title} 
            className="w-full h-full object-cover object-center" 
            loading="lazy" 
          />
        </div>
        <div>
          <div className="flex items-center mb-1 pt-[15px]">
            <Badge variant="outline" className={cn(badgeBorderColor, "bg-transparent text-xs font-normal px-2 py-0.5 h-4 text-[10px] rounded-[30px] mr-2")}>
              {story.category}
            </Badge>
            <span className={cn("text-[10px] flex items-center", metaTextColor)}>
              <Clock size={10} className="mr-1" />
              {story.readTime} min
            </span>
          </div>
          <h2 className="text-base py-[4px]">
            {story.title}
          </h2>
          {!hideAuthor && <p className={cn("text-xs", metaTextColor)}>
              By {story.author.name}
            </p>}
        </div>
      </Link>
    );
  }

  return (
    <Card className={cn("border-none overflow-hidden h-full rounded-none", featured && "row-span-1")}>
      <Link to={`/stories/${story.slug}`} className="block h-full group">
        <div className={cn(
          "relative overflow-hidden rounded-[2px] aspect-[3/2]"
        )}>
          <img 
            src={story.coverImage} 
            alt={story.title} 
            className="w-full h-full object-cover object-center" 
            loading="lazy" 
          />
          {featured && <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>}
          {featured && <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-center mb-3">
                <Badge variant="outline" className={cn(badgeBorderColor, "bg-transparent text-primary-foreground text-xs font-normal px-2 py-0.5 text-[11px] rounded-[30px]")}>
                  {story.category}
                </Badge>
                <span className={cn("ml-2 text-xs flex items-center", textColor)}>
                  <Clock size={12} className="mr-1" />
                  {story.readTime} min
                </span>
              </div>
              <h3 className={cn("text-xl text-white mb-2 line-clamp-2 transition-colors group-hover:underline", headerFontClass)}>
                {story.title}
              </h3>
            </div>}
        </div>
        <CardContent className="p-0 py-3">
          {!featured && <>
              <div className="flex items-center mb-2">
                <Badge variant="outline" className={cn(badgeBorderColor, "bg-transparent text-xs font-normal px-2 py-0.5 h-5 text-[11px] rounded-[30px]")}>
                  {story.category}
                </Badge>
                <span className={cn("ml-2 text-xs flex items-center", metaTextColor)}>
                  <Clock size={12} className="mr-1" />
                  {story.readTime} min
                </span>
              </div>
              <h3 className={cn("text-lg mb-2 line-clamp-2 leading-[1.4rem] group-hover:underline", textColor, headerFontClass)}>
                {story.title}
              </h3>
            </>}
          {!hideAuthor && <div className="flex items-center mb-4">
              <img src={story.author.avatar} alt={story.author.name} className="w-8 h-8 border border-gray-700 rounded-[2px]" />
              <span className={cn("text-sm ml-2", textColor)}>{story.author.name}</span>
            </div>}
          <p className={cn("line-clamp-3 text-[13px]", metaTextColor)}>
            {story.excerpt}
          </p>
        </CardContent>
      </Link>
    </Card>
  );
};

export default StoryCard;
